import React from 'react';
import PropTypes from 'prop-types';
import Switch from 'react-switch';
import './PublicShopSettingsPanel.scss';
import { confirmAlert } from 'react-confirm-alert';
import cn from 'classnames';

import CheckboxButton from '../../../General/Buttons/CheckboxButton';

import { getSettings, getUserTier, isAdmin } from '../../../../Helpers/user_helpers';

const PublicShopSettingsPanel = props => {
  const { user } = props;
  const {
    isHiddenFromPublicShop,
    productsVisibleInShop,
    showConsultsTabInShop,
    hideTrustCountFromShop,
    hidePricesOnShop,
    hideLatestFindsTimestampOnShop
  } = getSettings(user) || {};

  const toggleUserSetting = async (setting, value) => {
    await props.updateUserSettings({ [setting]: value });
  };

  let sections = [];

  /*
    Visible Products.

    Specify which products you want to show up on your "Popular" and "Latest" tabs.

      Option 1: All Products
      Option 2: Only Products I've Added to Visible Collections
  */
  const confirmAndCommitVisibilityChange = newValue => {
    confirmAlert({
      title: 'Just confirming.',
      message: {
        all: 'This will show all links regardless of visibility settings.',
        'visible-collections-and-quick-links': 'This will show products that are in visible collections and quick links.',
        'visible-collections-and-popular-quick-links':
          'This will show products that are in visible collections and quick links that have driven over 10 clicks. Quick link visibility with popularity may take up to 24 hours to show up.',
        'visible-collections': 'This will show products that are in collections and hide all quick links.'
      }[newValue],
      buttons: [
        { label: 'Cancel', className: 'cancel', onClick: () => {} },
        {
          label: 'Make Change',
          onClick: () => {
            toggleUserSetting('productsVisibleInShop', newValue);
            window.ALERT.info(
              {
                all: 'All links that are connected to popular products are now visible.',
                'visible-collections-and-quick-links': 'Successfully hid products that are not in visible collections and quick links.',
                'visible-collections-and-popular-quick-links':
                  'Successfully hid products that are not in collections or do not reach the 10 click threshold.',
                'visible-collections': 'Successfully hid products that are not in visible collections.'
              }[newValue] || 'Successfully updated visibility settings.'
            );
          }
        }
      ]
    });
  };

  sections.push({
    type: 'multi',
    title: 'Visible Products',
    subtitle: <>Specify which products you want to show up on your "Popular" and "Latest" tabs.</>,
    isHighlighted: window.location.hash === '#visibleproducts',
    options: [
      {
        title: 'Visible Collections and Quick Links (Recommended)',
        tooltip: 'This option will show products that are in collections and quick links.',
        value: productsVisibleInShop === 'visible-collections-and-quick-links',
        onChange: () => confirmAndCommitVisibilityChange('visible-collections-and-quick-links')
      },
      {
        title: 'Visible Collections and Popular Quick Links',
        tooltip:
          'This option will show products that are in collections and quick links that have driven over 10 clicks. Quick link visibility with popularity may take up to 24 hours to show up.',
        value: productsVisibleInShop === 'visible-collections-and-popular-quick-links',
        onChange: () => confirmAndCommitVisibilityChange('visible-collections-and-popular-quick-links')
      },
      {
        title: 'Visible Collections Only',
        tooltip: 'This option will show products that are in collections and hide all quick links.',
        value: productsVisibleInShop === 'visible-collections',
        onChange: () => confirmAndCommitVisibilityChange('visible-collections')
      },
      {
        title: 'All Links',
        tooltip: 'This option will show all links regardless of visibility settings.',
        value: productsVisibleInShop === 'all',
        onChange: () => confirmAndCommitVisibilityChange('all')
      }
    ]
  });

  /*
    Searchable in ShopMy.

    Specify whether the creator wants to show up on the publicly searchable ShopMy website.
  */
  const canEnableSearch = getUserTier(user).bestTier <= 2 || isAdmin(user);
  const isDiscoverable = canEnableSearch && !isHiddenFromPublicShop;
  sections.push({
    type: 'toggle',
    title: 'Discoverable on ShopMy',
    subtitle: isDiscoverable ? (
      <>You are currently discoverable on ShopMy search for shoppers to find your shop.</>
    ) : (
      <>You are currently not discoverable on ShopMy search for shoppers to find your shop.</>
    ),
    isHighlighted: window.location.hash === '#publicshopconfig',
    isActive: isDiscoverable,
    disabled: !canEnableSearch,
    onToggle: () => {
      if (!canEnableSearch) return window.ALERT.warn(`You must reach the trendsetter status to be findable via search.`);

      toggleUserSetting('isHiddenFromPublicShop', !isHiddenFromPublicShop);
      if (!isHiddenFromPublicShop) window.ALERT.info('Your shop will no longer be findable via search within 24 hours.');
      else window.ALERT.success('Your shop will be findable via search within 24 hours');
    }
  });

  /*
    Trusted By Count

    Specify whether you want to show the "Trusted By" count on your shop.
  */
  sections.push({
    type: 'toggle',
    title: '"Trusted By" Visibility',
    subtitle: hideTrustCountFromShop
      ? 'You are currently not showing the "Trusted By" count on your shop.'
      : 'You are currently showing the "Trusted By" count on your shop.',
    isActive: !hideTrustCountFromShop,
    onToggle: () => {
      toggleUserSetting('hideTrustCountFromShop', !hideTrustCountFromShop);
      hideTrustCountFromShop
        ? window.ALERT.success('The "Trusted By" count will now be visible on your shop.')
        : window.ALERT.info('The "Trusted By" count will no longer be visible on your shop.');
    }
  });

  /*
    Prices

    Specify whether you want to show prices on your shop result pages.
  */
  sections.push({
    type: 'toggle',
    title: 'Visible Prices',
    subtitle: hidePricesOnShop
      ? 'You are currently not showing prices on your shop result pages.'
      : 'You are currently showing prices on your shop result pages.',
    isActive: !hidePricesOnShop,
    onToggle: () => {
      toggleUserSetting('hidePricesOnShop', !hidePricesOnShop);
      hidePricesOnShop
        ? window.ALERT.success('Prices will now be visible on your shop result pages.')
        : window.ALERT.info('Prices will no longer be visible on your shop result pages.');
    }
  });

  /*
    Latest Finds Timestamps

    Specify whether you want to show timestamps on your Latest Finds section.
  */
  sections.push({
    type: 'toggle',
    title: 'Latest Finds Timestamps',
    subtitle: hideLatestFindsTimestampOnShop
      ? 'You are currently not showing timestamps on your Latest Finds section.'
      : 'You are currently showing timestamps on your Latest Finds section.',
    isActive: !hideLatestFindsTimestampOnShop,
    onToggle: () => {
      toggleUserSetting('hideLatestFindsTimestampOnShop', !hideLatestFindsTimestampOnShop);
      hideLatestFindsTimestampOnShop
        ? window.ALERT.success('Timestamps will now be visible on your Latest Finds section.')
        : window.ALERT.info('Timestamps will no longer be visible on your Latest Finds section.');
    }
  });

  /*
    Consults Visibility

    Specify whether you want to show the Consults tab on your shop page.
  */
  sections.push({
    type: 'toggle',
    title: 'Offering Consultations',
    subtitle: (
      <>
        Specify whether you want to offer Consults on your shop page. Learn more{' '}
        <a
          href='https://guide.shopmy.us/building-your-shop/43V9gf7Co5VBrmboNBWQ1S/what-are-consults/43V9gf7Co5yf1B5R9LFRHF'
          target='_blank'
          rel='noopener noreferrer'
        >
          here
        </a>
        .
      </>
    ),
    isActive: showConsultsTabInShop,
    onToggle: () => {
      !showConsultsTabInShop
        ? window.ALERT.success('The Consults tab will now be visible on your shop.')
        : window.ALERT.info('The Consults tab will no longer be visible on your shop.');
      toggleUserSetting('showConsultsTabInShop', !showConsultsTabInShop);
    }
  });

  return (
    <div className='shop-settings-panel-outer'>
      <div className='shop-settings-panel-inner'>
        {sections.map(section => {
          const { title, subtitle, isHighlighted, options } = section;

          if (section.type === 'multi') {
            return (
              <div className='settings-section' key={`location-panel-${title}`}>
                {title && (
                  <div
                    className={cn('settings-section-title', {
                      highlighted: isHighlighted
                    })}
                  >
                    {title}
                  </div>
                )}
                {subtitle && <div className='settings-section-subtitle'>{subtitle}</div>}

                <div className='settings-section-list'>
                  {options.map(option => {
                    const { title, userSettingsVariable, value } = option;
                    return (
                      <CheckboxButton
                        key={`location-panel-${userSettingsVariable}`}
                        isChecked={value}
                        isDisabled={option.isDisabled}
                        onChange={option.onChange}
                        text={title}
                      />
                    );
                  })}
                </div>
              </div>
            );
          } else if (section.type === 'toggle') {
            return (
              <div className='settings-section-toggle' key={`location-panel-${title}`}>
                <div className='settings-section-title-container'>
                  {title && <div className='settings-section-title'>{title}</div>}
                  {subtitle && <div className='settings-section-subtitle'>{subtitle}</div>}
                </div>
                <div className='settings-section-toggle-switch' onClick={section.onToggle}>
                  <Switch
                    onChange={() => {}}
                    checked={section.isActive}
                    height={14}
                    width={24}
                    onColor='#11835a'
                    offColor='#ccc'
                    className='switch'
                    checkedIcon={false}
                    uncheckedIcon={false}
                  />
                </div>
              </div>
            );
          }
        })}
      </div>
    </div>
  );
};

PublicShopSettingsPanel.propTypes = {
  user: PropTypes.object.isRequired,
  updateUserSettings: PropTypes.func.isRequired
};

export default PublicShopSettingsPanel;
